import type { Comment } from '@/@types/comment'
import { AttachmentStatus, AttachmentType, generateUniID } from '@/states/attachment'
import { css } from '@emotion/react'
import { first } from 'lodash-es'
import {
	type ClipboardEvent,
	type ClipboardEventHandler,
	forwardRef,
	useCallback,
	useRef,
} from 'react'
import { type ControllerRenderProps, useFormContext } from 'react-hook-form'
import LexicalEditor, { type LexicalEditorHandle } from '../Editor/LexicalEditor'
import { CommentFormValue } from './types'

export interface CommentContentEditorProps
	extends Omit<ControllerRenderProps<CommentFormValue, 'content'>, 'ref'> {
	to: Comment | undefined

	/**
	 * @default false
	 */
	autoFocus?: boolean
}

export default forwardRef<LexicalEditorHandle, CommentContentEditorProps>(
	function CommentContentEditor(
		{ to: toComment, autoFocus = false, ...props }: CommentContentEditorProps,
		ref
	) {
		const isCompositingRef = useRef<boolean>(false)
		const { setValue, getValues } = useFormContext<CommentFormValue>()
		const isReplyMode = !!toComment

		const handleTextareaPaste: ClipboardEventHandler<HTMLElement> = useCallback(
			(e: ClipboardEvent<HTMLTextAreaElement>) => {
				const file = first(e.clipboardData.files)
				const dataTransferItem = first(e.clipboardData.items)

				if (!file || !dataTransferItem) {
					return
				}

				if (dataTransferItem.kind === 'file') {
					e.preventDefault()

					if (['image/png', 'image/jpeg'].includes(dataTransferItem.type)) {
						setValue('attachments', [
							...getValues('attachments'),
							{
								id: generateUniID(),
								file,
								type: AttachmentType.PICTURE,
								status: AttachmentStatus.WAIT_TO_UPLOAD,
							},
						])
					}
				}
			},
			[getValues, setValue]
		)

		return (
			<LexicalEditor
				{...props}
				ref={ref}
				spellCheck={false}
				placeholder={
					isReplyMode ? `回复给 ${toComment.user.screenName}...` : '友善发言的人运气都不会太差'
				}
				onPaste={handleTextareaPaste}
				data-autofocus={autoFocus}
				onCompositionStart={() => {
					isCompositingRef.current = true
				}}
				onCompositionEnd={() => {
					isCompositingRef.current = false
				}}
				css={css`
					margin-top: 6px;
					font-size: 16px;
					line-height: 24px;

					&::placeholder {
						color: var(--tint-tertiary);
					}
				`}
			/>
		)
	}
)
